import React, { useMemo } from 'react';
import { SpineAsset } from '../../../core/types/spine.types';

interface SpineDebugOverlayProps {
  type: 'bones' | 'slots' | 'boundingBoxes';
  asset: SpineAsset;
  width: number;
  height: number;
  zoom: number;
}

export const SpineDebugOverlay: React.FC<SpineDebugOverlayProps> = ({
  type,
  asset,
  width,
  height,
  zoom,
}) => {
  // Calculate bone positions (simplified visualization)
  const elements = useMemo(() => {
    const centerX = width / 2;
    const centerY = height / 2;
    const scale = Math.min(width, height) / Math.max(asset.width || 100, asset.height || 100);

    if (type === 'bones') {
      return asset.bones.map((bone, i) => {
        // Calculate bone position relative to center
        const x = centerX + bone.x * scale;
        const y = centerY - bone.y * scale; // Spine Y is up, screen Y is down

        return (
          <g key={bone.name}>
            {/* Bone joint */}
            <circle
              cx={x}
              cy={y}
              r={3 / zoom}
              fill="#f97316"
              stroke="#fff"
              strokeWidth={1 / zoom}
            />
            {/* Bone name (on hover would be better) */}
            {i < 10 && ( // Only show first 10 labels to avoid clutter
              <text
                x={x + 5 / zoom}
                y={y - 5 / zoom}
                fontSize={10 / zoom}
                fill="#f97316"
              >
                {bone.name}
              </text>
            )}
          </g>
        );
      });
    }

    if (type === 'slots') {
      return asset.slots.map((slot) => {
        // Find the bone this slot is attached to
        const bone = asset.bones.find((b) => b.name === slot.boneName);
        if (!bone) return null;

        const x = centerX + bone.x * scale;
        const y = centerY - bone.y * scale;

        return (
          <g key={slot.name}>
            <rect
              x={x - 5 / zoom}
              y={y - 5 / zoom}
              width={10 / zoom}
              height={10 / zoom}
              fill="none"
              stroke="#3b82f6"
              strokeWidth={1 / zoom}
            />
          </g>
        );
      });
    }

    if (type === 'boundingBoxes') {
      // Would need to extract bounding box attachments from skins
      return (
        <rect
          x={centerX - (asset.width * scale) / 2}
          y={centerY - (asset.height * scale) / 2}
          width={asset.width * scale}
          height={asset.height * scale}
          fill="none"
          stroke="#10b981"
          strokeWidth={1 / zoom}
          strokeDasharray={`${4 / zoom} ${2 / zoom}`}
        />
      );
    }

    return null;
  }, [type, asset, width, height, zoom]);

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{ width, height }}
    >
      {elements}
    </svg>
  );
};
